import Chart, { useChart } from 'src/components/chart';
import { Box, Stack, Typography, Skeleton } from '@mui/material';
import { useLocales } from 'src/locales';
import { fDate } from 'src/utils/format-time';
import CiCard from './CiCard';
import CustomTooltip from './CustomTooltip';

const SOLO_BIDDER_CHART_COLOR = '#C09910';

export interface SoloBidderWeeklyDatum {
  week_start: string;
  solo_bidder: number;
}

interface SoloBidderRateCardProps {
  title: string;
  dateRange: string;
  /** Current value as percentage (e.g. 12 for "12%") */
  value?: number | null;
  /** Weekly time series for the line chart */
  weeklySeries?: SoloBidderWeeklyDatum[];
  loading?: boolean;
  /** When true, render only header + content (non-brand structure) for use inside non-brand card wrapper + footer */
  contentOnly?: boolean;
}

export default function SoloBidderRateCard({
  title,
  dateRange,
  value,
  weeklySeries = [],
  loading = false,
  contentOnly = false,
}: SoloBidderRateCardProps) {
  const { t } = useLocales();

  const displayValue =
    value != null && !Number.isNaN(value)
      ? `${Math.round(value)}%`
      : undefined;

  const hasWeeklyValues = weeklySeries.some(
    (d) => Number(d.solo_bidder) > 0
  );

  const hasDisplayValue =
    value != null &&
    Number.isFinite(Number(value)) &&
    Number(value) > 0;

  const hasNoData = !hasWeeklyValues && !hasDisplayValue;

  // Same chart styling as non-brand visibility: area chart with gradient fill (lighter shadow)
  const chartOptions = useChart({
    chart: {
      type: 'area',
      toolbar: { show: false },
    },
    stroke: {
      width: 2,
      curve: 'smooth',
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    colors: [SOLO_BIDDER_CHART_COLOR],
    xaxis: {
      type: 'category',
      categories: weeklySeries.map((d) => d.week_start),
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { show: false },
    },
    grid: {
      show: false,
      padding: { left: 12, right: 12, top: 12, bottom: 12 },
    },
    legend: { show: false },
    tooltip: {
      enabled: true,
      x: { show: false, formatter: () => '' },
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const point = weeklySeries[dataPointIndex];
        if (!point) return '';
        const formattedDate = point.week_start ? fDate(point.week_start) : '';
        const pct = Math.round((point.solo_bidder ?? 0) * 10) / 10;
        return `
          <div class="solo-bidder-tooltip-content" style="
            padding: 0;
            border-radius: 8px;
            background: #FCFCFD;
            box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.20);
            min-width: 200px;
            font-family: 'Public Sans', sans-serif;
          ">
            <div style="
              padding: 12px;
              background: rgba(226, 229, 233, 0.16);
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 24px;
            ">
              <span style="color: #637381; font-size: 14px; font-weight: 700; line-height: 1;">${title}</span>
              <span style="color: #637381; font-size: 14px; font-weight: 400; opacity: 0.9; margin-left: auto;">${formattedDate}</span>
            </div>
            <div style="padding: 12px; display: flex; align-items: center; gap: 8px;">
              <div style="width: 6px; height: 20px; border-radius: 2px; background: ${SOLO_BIDDER_CHART_COLOR}; flex-shrink: 0;"></div>
              <span style="color: #637381; font-size: 14px; font-weight: 400; flex: 1;">${title}</span>
              <span style="color: #333C47; font-size: 14px; font-weight: 500;">${pct}%</span>
            </div>
          </div>
        `;
      },
    },
  });

  const series = [
    {
      name: title,
      data: weeklySeries.map((d) => Math.round((d.solo_bidder ?? 0) * 10) / 10),
    },
  ];

  // Same header pattern as non-brand visibility: indicator line + label (no value in title; value is topMetric on right)
  const titleWithIndicator = (
    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1}>
      <svg
        className="card-top-header-item-indicator"
        xmlns="http://www.w3.org/2000/svg"
        width={14}
        height={14}
        viewBox="0 0 14 14"
        fill="none"
        style={{ flexShrink: 0 }}
      >
        <line
          x1={1}
          y1={7}
          x2={13}
          y2={7}
          stroke={SOLO_BIDDER_CHART_COLOR}
          strokeWidth={2}
          strokeLinecap="round"
        />
      </svg>
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: '#333C47',
          lineHeight: 1,
          fontFamily: 'Public Sans, sans-serif',
        }}
      >
        {title}
      </Typography>
    </Stack>
  );

  function mainContent() {
    if (hasNoData) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 120,
            marginY: '30px',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            {t('dashboard.global.no_data_available')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
            {t('dashboard.global.visibility.no_results_found_description')}
          </Typography>
        </Box>
      );
    }

    if (weeklySeries.length === 0) {
      return <Box sx={{ width: '100%', height: 70 }} />;
    }

    return (
      <Chart
        type="area"
        series={series}
        options={chartOptions}
        sx={{ width: '100%', height: 70 }}
        height={70}
      />
    );
  }

  const soloBidderRateTooltip = t('dashboard.global.visibility.tooltips.soloBidderRate');

  if (contentOnly) {
    const chartBody =
      hasNoData ? (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <Typography variant="body2" color="text.secondary">{t('dashboard.global.no_data_available')}</Typography>
        </Stack>
      ) : weeklySeries.length === 0 ? (
        <Box sx={{ width: '100%', height: 70 }} />
      ) : (
        <Chart type="area" series={series} options={chartOptions} sx={{ width: '100%' }} height={149} />
      );
    return (
      <>
        <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
          <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1}>
            <CustomTooltip title={soloBidderRateTooltip} placement="top">
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {titleWithIndicator}
              </Box>
            </CustomTooltip>
            <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
              {displayValue}
            </Typography>
          </Stack>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : (
          <Stack width="inherit" flex={1} height={149}>
            {chartBody}
          </Stack>
        )}
      </>
    );
  }

  return (
    <CiCard
      dateRange={dateRange}
      title={titleWithIndicator}
      titleTooltip={soloBidderRateTooltip}
      topMetric={displayValue}
      mainContent={mainContent()}
      loading={loading}
    />
  );
}
